import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "../../services/api";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { SectionCard } from "../../components/admin/AdminCard";
import toast from "react-hot-toast";

type Variable = { key: string; label: string; sample: string };
type CustomVariable = { key: string; label: string; default: string };

/** Marks the template the automatic club upgrade issues. */
const ACTIVATION_PURPOSE = "club_basic_activation";

type Template = {
  id: string;
  name: string;
  description: string | null;
  body_html: string;
  title_text: string | null;
  footer_text: string | null;
  custom_variables: CustomVariable[] | null;
  active: boolean;
  purpose: string | null;
  created_at: string;
};

type Club = {
  id: string;
  name: string;
  public_name: string | null;
  city: string | null;
  county: string | null;
  cui: string | null;
  status: string;
  admin_email: string | null;
  admin_name: string | null;
};

type Contract = {
  id: string;
  number: string;
  title: string;
  status: string;
  sent_to: string | null;
  sent_at: string | null;
  error: string | null;
  created_at: string;
  club: { id: string; name: string; public_name: string | null } | null;
  template: { id: string; name: string } | null;
};

const inputClass =
  "w-full px-3 py-2 text-sm border rounded-lg border-slate-300 bg-white text-slate-800 placeholder-slate-400";

const EMPTY_TEMPLATE = {
  id: null as string | null,
  name: "",
  description: "",
  body_html: "",
  title_text: "",
  footer_text: "",
  custom_variables: [] as CustomVariable[],
  active: true,
  purpose: null as string | null,
};

type Draft = typeof EMPTY_TEMPLATE;

function fmt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(
    d.getHours()
  ).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const STATUS: Record<string, { label: string; cls: string }> = {
  sent: { label: "Trimis", cls: "bg-emerald-100 text-emerald-700" },
  pending: { label: "În curs", cls: "bg-amber-100 text-amber-700" },
  failed: { label: "Eșuat", cls: "bg-rose-100 text-rose-700" },
};

/**
 * Contract templates and the contracts generated from them.
 *
 * A template carries {{placeholders}}; sending one against a club fills them
 * from that club's record, renders a PDF and emails it. The sent document is
 * stored as rendered, so editing a template later never changes what somebody
 * already received.
 */
export default function Contracts() {
  const [tab, setTab] = useState<"send" | "templates" | "history">("send");

  const [templates, setTemplates] = useState<Template[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [variables, setVariables] = useState<Variable[]>([]);
  const [loading, setLoading] = useState(true);

  // --- template editor ---
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // --- send form ---
  const [sendTemplateId, setSendTemplateId] = useState("");
  const [sendClubId, setSendClubId] = useState("");
  const [sendTo, setSendTo] = useState("");
  const [sendMessage, setSendMessage] = useState("");
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);

  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, c, h, v] = await Promise.all([
        api.get<{ templates: Template[] }>("/contracts/templates"),
        api.get<{ clubs: Club[] }>("/contracts/clubs"),
        api.get<{ contracts: Contract[] }>("/contracts"),
        api.get<{ variables: Variable[] }>("/contracts/variables"),
      ]);
      setTemplates(t.data.templates || []);
      setClubs(c.data.clubs || []);
      setContracts(h.data.contracts || []);
      setVariables(v.data.variables || []);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-au putut încărca datele.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === sendTemplateId) || null,
    [templates, sendTemplateId]
  );
  const selectedClub = useMemo(() => clubs.find((c) => c.id === sendClubId) || null, [clubs, sendClubId]);

  // Picking a club prefills the recipient with its admin; the admin can still
  // type a different address.
  useEffect(() => {
    setSendTo(selectedClub?.admin_email || "");
  }, [selectedClub]);

  // Custom placeholders start from the template's defaults each time the
  // template changes, so leftovers from a previous one cannot leak through.
  useEffect(() => {
    const custom = selectedTemplate?.custom_variables || [];
    const next: Record<string, string> = {};
    for (const v of custom) next[v.key] = v.default || "";
    setOverrides(next);
  }, [selectedTemplate]);

  // ------------------------------------------------------------- templates --

  const openNew = () => setDraft({ ...EMPTY_TEMPLATE });
  const openEdit = (t: Template) =>
    setDraft({
      id: t.id,
      name: t.name,
      description: t.description || "",
      body_html: t.body_html,
      title_text: t.title_text || "",
      footer_text: t.footer_text || "",
      custom_variables: t.custom_variables || [],
      active: t.active,
      purpose: t.purpose || null,
    });

  const importFile = async (file: File) => {
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post<{ body_html: string; warnings: string[] }>("/contracts/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDraft((d) => ({ ...(d || EMPTY_TEMPLATE), body_html: data.body_html }));
      if (data.warnings?.length) {
        toast(`Document importat, cu ${data.warnings.length} avertisment(e) de conversie.`, { icon: "⚠️" });
      } else {
        toast.success("Document importat.");
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut citi fișierul.");
    }
    setImporting(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const saveTemplate = async () => {
    if (!draft) return;
    if (!draft.name.trim()) return toast.error("Numele modelului este obligatoriu.");
    if (!draft.body_html.trim()) return toast.error("Conținutul contractului este obligatoriu.");
    setSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim(),
        body_html: draft.body_html,
        title_text: draft.title_text.trim(),
        footer_text: draft.footer_text.trim(),
        custom_variables: draft.custom_variables,
        active: draft.active,
        purpose: draft.purpose,
      };
      if (draft.id) await api.put(`/contracts/templates/${draft.id}`, payload);
      else await api.post("/contracts/templates", payload);
      toast.success(draft.id ? "Model actualizat." : "Model salvat.");
      setDraft(null);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut salva modelul.");
    }
    setSaving(false);
  };

  const deleteTemplate = async (id: string) => {
    try {
      await api.delete(`/contracts/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      toast.success("Model șters.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut șterge modelul.");
    }
    setPendingDelete(null);
  };

  const addCustomVariable = () => {
    if (!draft) return;
    setDraft({ ...draft, custom_variables: [...draft.custom_variables, { key: "", label: "", default: "" }] });
  };

  // ------------------------------------------------------------------ send --

  /** Open a generated PDF in a new tab. Both preview and download use this. */
  const openPdf = async (request: () => Promise<any>, fallback: string) => {
    try {
      const res = await request();
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) toast.error("Permite ferestrele pop-up pentru a vedea documentul.");
      // Give the tab time to load before releasing the object URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e: any) {
      // An error response is a JSON blob, not a PDF — read it back as text.
      let message = fallback;
      try {
        const text = await e?.response?.data?.text?.();
        if (text) message = JSON.parse(text)?.error || message;
      } catch {
        /* keep the fallback */
      }
      toast.error(message);
    }
  };

  const preview = async () => {
    if (!sendTemplateId) return toast.error("Selectează un model de contract.");
    setPreviewing(true);
    await openPdf(
      () =>
        api.post(
          "/contracts/preview",
          { template_id: sendTemplateId, club_id: sendClubId || null, variables: overrides },
          { responseType: "blob" }
        ),
      "Nu am putut genera previzualizarea."
    );
    setPreviewing(false);
  };

  const send = async () => {
    if (!sendTemplateId) return toast.error("Selectează un model de contract.");
    if (!sendClubId) return toast.error("Selectează clubul destinatar.");
    if (!sendTo.trim()) return toast.error("Completează adresa destinatarului.");
    setSending(true);
    try {
      await api.post("/contracts/send", {
        template_id: sendTemplateId,
        club_id: sendClubId,
        to: sendTo.trim(),
        message: sendMessage.trim(),
        variables: overrides,
      });
      toast.success("Contract trimis.");
      setSendMessage("");
      await load();
      setTab("history");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu am putut trimite contractul.");
    }
    setSending(false);
  };

  const deleteContract = async (id: string) => {
    try {
      await api.delete(`/contracts/${id}`);
      setContracts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Contract șters din istoric.");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Nu s-a putut șterge contractul.");
    }
    setPendingDelete(null);
  };

  const tabClass = (active: boolean) =>
    `px-4 py-2 text-sm font-semibold rounded-xl transition ${
      active ? "bg-indigo-600 text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
    }`;

  const customForTemplate = selectedTemplate?.custom_variables || [];

  return (
    <div className="mx-auto max-w-[1500px] space-y-6">
      <AdminPageHeader
        title="Contracte"
        subtitle="Încarcă un model de contract, completează-l cu datele clubului și trimite-l în PDF."
        actions={
          tab === "templates" ? <AdminButton onClick={openNew}>Model nou</AdminButton> : undefined
        }
      />

      <div className="flex flex-wrap gap-2">
        <button type="button" className={tabClass(tab === "send")} onClick={() => setTab("send")}>
          Trimite contract
        </button>
        <button type="button" className={tabClass(tab === "templates")} onClick={() => setTab("templates")}>
          Modele ({templates.length})
        </button>
        <button type="button" className={tabClass(tab === "history")} onClick={() => setTab("history")}>
          Istoric ({contracts.length})
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Se încarcă...</p>
      ) : tab === "send" ? (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            <SectionCard title="Contract" bodyPadded>
              {templates.length === 0 ? (
                <p className="text-sm text-slate-500">
                  Nu există niciun model. Creează unul în tabul <strong>Modele</strong>.
                </p>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">Model de contract</label>
                    <select
                      value={sendTemplateId}
                      onChange={(e) => setSendTemplateId(e.target.value)}
                      className={inputClass}
                    >
                      <option value="">Alege un model...</option>
                      {templates
                        .filter((t) => t.active)
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">Club destinatar</label>
                    <select value={sendClubId} onChange={(e) => setSendClubId(e.target.value)} className={inputClass}>
                      <option value="">Alege un club...</option>
                      {clubs.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.public_name || c.name}
                          {c.city ? ` — ${c.city}` : ""}
                          {c.admin_email ? "" : " (fără email admin)"}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">Trimite la</label>
                    <input
                      type="email"
                      value={sendTo}
                      onChange={(e) => setSendTo(e.target.value)}
                      placeholder="adresa@club.ro"
                      className={inputClass}
                    />
                    <p className="mt-1 text-xs text-slate-400">
                      Precompletat cu adresa administratorului clubului. Poți trimite la altă adresă.
                    </p>
                  </div>

                  <div>
                    <label className="block mb-1 text-sm font-medium text-slate-700">
                      Mesaj în email <span className="font-normal text-slate-400">(opțional)</span>
                    </label>
                    <textarea
                      rows={3}
                      value={sendMessage}
                      onChange={(e) => setSendMessage(e.target.value)}
                      className={inputClass}
                      placeholder="Un rând de context pentru club..."
                    />
                  </div>

                  {customForTemplate.length > 0 && (
                    <div className="pt-2 border-t border-slate-100">
                      <p className="mb-2 text-sm font-semibold text-slate-700">Variabile proprii</p>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {customForTemplate.map((v) => (
                          <div key={v.key}>
                            <label className="block mb-1 text-xs font-medium text-slate-600">
                              {v.label} <code className="text-slate-400">{`{{${v.key}}}`}</code>
                            </label>
                            <input
                              type="text"
                              value={overrides[v.key] ?? ""}
                              onChange={(e) => setOverrides((o) => ({ ...o, [v.key]: e.target.value }))}
                              className={inputClass}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 pt-2">
                    <AdminButton variant="secondary" onClick={preview} disabled={previewing}>
                      {previewing ? "Se generează..." : "Previzualizează PDF"}
                    </AdminButton>
                    <AdminButton onClick={send} disabled={sending}>
                      {sending ? "Se trimite..." : "Trimite contractul"}
                    </AdminButton>
                  </div>
                </div>
              )}
            </SectionCard>
          </div>

          <SectionCard title="Datele clubului" subtitle="Valorile care vor înlocui variabilele" bodyPadded>
            {!selectedClub ? (
              <p className="text-sm text-slate-500">Selectează un club pentru a vedea datele care se completează.</p>
            ) : (
              <dl className="space-y-2 text-sm">
                {[
                  ["Denumire", selectedClub.public_name || selectedClub.name],
                  ["CUI", selectedClub.cui],
                  ["Oraș", selectedClub.city],
                  ["Județ", selectedClub.county],
                  ["Administrator", selectedClub.admin_name],
                  ["Email", selectedClub.admin_email],
                ].map(([label, value]) => (
                  <div key={String(label)} className="flex gap-2">
                    <dt className="w-32 shrink-0 text-slate-400">{label}</dt>
                    <dd className={`min-w-0 break-words ${value ? "text-slate-700" : "text-rose-500"}`}>
                      {value || "lipsește"}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
            <p className="pt-3 mt-3 text-xs border-t text-slate-400 border-slate-100">
              Câmpurile lipsă apar în contract ca „............”, nu se închid tacit — un spațiu gol este vizibil, o
              valoare care dispare nu.
            </p>
          </SectionCard>
        </div>
      ) : tab === "templates" ? (
        <SectionCard title="Modele de contract">
          {templates.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-500">
              Niciun model încă. Apasă <strong>Model nou</strong> și încarcă un fișier .docx sau scrie contractul
              direct.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                    <th className="px-5 py-2 text-left">Nume</th>
                    <th className="py-2 pr-4 text-left">Descriere</th>
                    <th className="py-2 pr-4 text-left">Variabile proprii</th>
                    <th className="py-2 pr-4 text-left">Stare</th>
                    <th className="py-2 pr-5 text-right">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-t border-slate-100">
                      <td className="px-5 py-3 font-semibold text-slate-800">{t.name}</td>
                      <td className="py-3 pr-4 text-slate-500">{t.description || "—"}</td>
                      <td className="py-3 pr-4 text-slate-500 tabular-nums">{(t.custom_variables || []).length}</td>
                      <td className="py-3 pr-4">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            t.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"
                          }`}
                        >
                          {t.active ? "Activ" : "Inactiv"}
                        </span>
                        {t.purpose === ACTIVATION_PURPOSE && (
                          <span className="inline-block px-2 py-0.5 ml-2 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">
                            Activare Baza
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-5 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEdit(t)}
                          className="px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                        >
                          Editează
                        </button>
                        {pendingDelete === t.id ? (
                          <>
                            <button
                              type="button"
                              onClick={() => deleteTemplate(t.id)}
                              className="px-3 py-1.5 text-xs font-bold text-white bg-rose-600 rounded-lg"
                            >
                              Confirmi?
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingDelete(null)}
                              className="px-2 py-1.5 text-xs text-slate-500"
                            >
                              Nu
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setPendingDelete(t.id)}
                            className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-rose-600"
                          >
                            Șterge
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      ) : (
        <SectionCard title="Contracte trimise">
          {contracts.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-500">Niciun contract trimis încă.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-semibold tracking-wide uppercase text-slate-400">
                    <th className="px-5 py-2 text-left">Număr</th>
                    <th className="py-2 pr-4 text-left">Club</th>
                    <th className="py-2 pr-4 text-left">Trimis la</th>
                    <th className="py-2 pr-4 text-left">Data</th>
                    <th className="py-2 pr-4 text-left">Stare</th>
                    <th className="py-2 pr-5 text-right">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => {
                    const badge = STATUS[c.status] || STATUS.pending;
                    return (
                      <tr key={c.id} className="border-t border-slate-100">
                        <td className="px-5 py-3 font-semibold text-slate-800 tabular-nums">{c.number}</td>
                        <td className="py-3 pr-4 text-slate-700">{c.club?.public_name || c.club?.name || "—"}</td>
                        <td className="py-3 pr-4 text-slate-500">{c.sent_to || "—"}</td>
                        <td className="py-3 pr-4 text-slate-500 tabular-nums">{fmt(c.sent_at || c.created_at)}</td>
                        <td className="py-3 pr-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${badge.cls}`}>
                            {badge.label}
                          </span>
                          {c.error && <p className="mt-1 text-xs text-rose-500 max-w-[240px]">{c.error}</p>}
                        </td>
                        <td className="py-3 pr-5 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() =>
                              openPdf(
                                () => api.get(`/contracts/${c.id}/pdf`, { responseType: "blob" }),
                                "Nu am putut deschide documentul."
                              )
                            }
                            className="px-3 py-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700"
                          >
                            Vezi PDF
                          </button>
                          {pendingDelete === c.id ? (
                            <>
                              <button
                                type="button"
                                onClick={() => deleteContract(c.id)}
                                className="px-3 py-1.5 text-xs font-bold text-white bg-rose-600 rounded-lg"
                              >
                                Confirmi?
                              </button>
                              <button
                                type="button"
                                onClick={() => setPendingDelete(null)}
                                className="px-2 py-1.5 text-xs text-slate-500"
                              >
                                Nu
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => setPendingDelete(c.id)}
                              className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-rose-600"
                            >
                              Șterge
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}

      {/* ------------------------------------------------ template editor -- */}
      {draft && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50">
          <div className="w-full max-w-4xl my-8 bg-white rounded-2xl">
            <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">
                {draft.id ? "Editează modelul" : "Model nou de contract"}
              </h2>
              <button
                type="button"
                onClick={() => setDraft(null)}
                className="ml-auto text-2xl leading-none text-slate-400 hover:text-slate-600"
                aria-label="Închide"
              >
                ×
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-700">Nume model</label>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    className={inputClass}
                    placeholder="Contract de colaborare"
                  />
                </div>
                <div>
                  <label className="block mb-1 text-sm font-medium text-slate-700">
                    Titlu în document <span className="font-normal text-slate-400">(opțional)</span>
                  </label>
                  <input
                    type="text"
                    value={draft.title_text}
                    onChange={(e) => setDraft({ ...draft, title_text: e.target.value })}
                    className={inputClass}
                    placeholder="Implicit: numele modelului"
                  />
                </div>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Descriere internă</label>
                <input
                  type="text"
                  value={draft.description}
                  onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div className="p-3 border rounded-xl border-slate-200 bg-slate-50">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".docx,.html,.htm,.txt,.md"
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) importFile(f);
                    }}
                  />
                  <AdminButton variant="secondary" onClick={() => fileRef.current?.click()} disabled={importing}>
                    {importing ? "Se importă..." : "Încarcă document"}
                  </AdminButton>
                  <span className="text-xs text-slate-500">
                    .docx, .html sau .txt — conținutul înlocuiește textul de mai jos.
                  </span>
                </div>
                <p className="mt-2 text-xs text-slate-400">
                  Un .doc vechi sau un PDF nu pot fi citite. Salvează documentul ca .docx înainte de a-l încărca.
                </p>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">Conținut contract</label>
                <textarea
                  rows={16}
                  value={draft.body_html}
                  onChange={(e) => setDraft({ ...draft, body_html: e.target.value })}
                  className={`${inputClass} font-mono text-xs leading-relaxed`}
                  placeholder="<p>Între {{club_legal_name}}, CUI {{club_cui}}, ...</p>"
                />
                <p className="mt-1 text-xs text-slate-400">
                  Se acceptă titluri, paragrafe, liste, bold și linii separatoare. Tabelele devin câte un rând de text.
                </p>
              </div>

              <div>
                <label className="block mb-1 text-sm font-medium text-slate-700">
                  Subsol document <span className="font-normal text-slate-400">(opțional)</span>
                </label>
                <input
                  type="text"
                  value={draft.footer_text}
                  onChange={(e) => setDraft({ ...draft, footer_text: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div className="p-3 border rounded-xl border-slate-200">
                <p className="mb-2 text-sm font-semibold text-slate-700">Variabile disponibile</p>
                <div className="flex flex-wrap gap-1.5">
                  {variables.map((v) => (
                    <button
                      key={v.key}
                      type="button"
                      title={`${v.label} — ex: ${v.sample}`}
                      onClick={() =>
                        setDraft((d) => (d ? { ...d, body_html: `${d.body_html}{{${v.key}}}` } : d))
                      }
                      className="px-2 py-1 font-mono text-xs rounded-md bg-slate-100 text-slate-600 hover:bg-indigo-100 hover:text-indigo-700"
                    >
                      {`{{${v.key}}}`}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-3 border rounded-xl border-slate-200">
                <div className="flex items-center gap-3 mb-2">
                  <p className="text-sm font-semibold text-slate-700">Variabile proprii</p>
                  <button
                    type="button"
                    onClick={addCustomVariable}
                    className="px-2 py-1 ml-auto text-xs font-semibold text-indigo-600 rounded-md hover:bg-indigo-50"
                  >
                    + Adaugă
                  </button>
                </div>
                {draft.custom_variables.length === 0 ? (
                  <p className="text-xs text-slate-400">
                    Pentru valori care nu vin din fișa clubului — durată, tarif, dată de început. Le completezi la
                    trimitere.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {draft.custom_variables.map((cv, i) => (
                      <div key={i} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
                        <input
                          type="text"
                          value={cv.key}
                          onChange={(e) => {
                            const next = [...draft.custom_variables];
                            next[i] = { ...cv, key: e.target.value };
                            setDraft({ ...draft, custom_variables: next });
                          }}
                          className={`${inputClass} font-mono`}
                          placeholder="cheie"
                        />
                        <input
                          type="text"
                          value={cv.label}
                          onChange={(e) => {
                            const next = [...draft.custom_variables];
                            next[i] = { ...cv, label: e.target.value };
                            setDraft({ ...draft, custom_variables: next });
                          }}
                          className={inputClass}
                          placeholder="Etichetă"
                        />
                        <input
                          type="text"
                          value={cv.default}
                          onChange={(e) => {
                            const next = [...draft.custom_variables];
                            next[i] = { ...cv, default: e.target.value };
                            setDraft({ ...draft, custom_variables: next });
                          }}
                          className={inputClass}
                          placeholder="Valoare implicită"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setDraft({
                              ...draft,
                              custom_variables: draft.custom_variables.filter((_, j) => j !== i),
                            })
                          }
                          className="px-2 text-sm text-slate-400 hover:text-rose-600"
                          aria-label="Elimină"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={draft.active}
                  onChange={(e) => setDraft({ ...draft, active: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                />
                Activ (poate fi selectat la trimitere)
              </label>

              {/*
                The one template the self-serve upgrade issues. Without one
                marked, a club that presses "Activează planul Baza" falls back
                to opening a support ticket — nothing breaks, but nothing is
                automatic either.
              */}
              <label className="flex items-start gap-2 p-3 text-sm border rounded-lg bg-indigo-50 border-indigo-200 text-slate-700">
                <input
                  type="checkbox"
                  checked={draft.purpose === ACTIVATION_PURPOSE}
                  onChange={(e) => setDraft({ ...draft, purpose: e.target.checked ? ACTIVATION_PURPOSE : null })}
                  className="w-4 h-4 mt-0.5 rounded border-slate-300"
                />
                <span>
                  <span className="font-semibold">Contract de activare plan Baza</span>
                  <span className="block mt-0.5 text-xs text-slate-600">
                    Acesta e documentul pe care îl primește un club când își activează singur planul.
                    Un singur model poate avea acest rol — bifarea îl ia de la celălalt.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex gap-2 px-5 py-4 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setDraft(null)}
                disabled={saving}
                className="flex-1 py-2.5 text-sm font-semibold border rounded-lg border-slate-300 text-slate-600 disabled:opacity-60"
              >
                Anulează
              </button>
              <button
                type="button"
                onClick={saveTemplate}
                disabled={saving}
                className="flex-1 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-lg disabled:opacity-60"
              >
                {saving ? "Se salvează..." : "Salvează modelul"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
